import React, { FC, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { SalesNoteJournalItem } from "interface/sales";
import { GrideDateFormatter } from "ui/Date";

interface SalesNoteJournalMobileListProps {
    items: SalesNoteJournalItem[];
    loading: boolean;
    onLoadMore: () => void;
}

const formatWeight = (value?: number | null) => {
    const asNumber = Number(value || 0);
    return `${asNumber.toLocaleString("no-NO", { maximumFractionDigits: 2 })} kg`;
};

const formatCurrency = (value?: number | null) => {
    const asNumber = Number(value || 0);
    return asNumber.toLocaleString("no-NO", {
        maximumFractionDigits: 2,
        style: "currency",
        currency: "NOK",
    });
};

const FieldFull: FC<{ label: string; value: string; hideEmpty?: boolean }> = ({
    label,
    value,
    hideEmpty = true,
}) => {
    if (hideEmpty && !value?.trim()) return null;
    return (
        <div className="fwsm-row-full">
            <span className="fwsm-label">{label}</span>
            <strong className="fwsm-value">{value}</strong>
        </div>
    );
};

const FieldSplit: FC<{
    leftLabel: string;
    leftValue: string;
    rightLabel: string;
    rightValue: string;
}> = ({ leftLabel, leftValue, rightLabel, rightValue }) => (
    <div className="fwsm-row-split">
        <div className="fwsm-col-left">
            <span className="fwsm-label">{leftLabel}</span>
            <strong className="fwsm-value">{leftValue}</strong>
        </div>
        <div className="fwsm-col-right">
            <span className="fwsm-label">{rightLabel}</span>
            <strong className="fwsm-value">{rightValue}</strong>
        </div>
    </div>
);

const SalesNoteJournalMobileList: FC<SalesNoteJournalMobileListProps> = ({
    items,
    loading,
    onLoadMore,
}) => {
    const { t } = useTranslation();

    const empty = useMemo(() => !loading && items.length === 0, [items.length, loading]);

    useEffect(() => {
        const handleWindowScroll = () => {
            const remaining =
                document.documentElement.scrollHeight - window.innerHeight - window.scrollY;
            if (remaining < 200) {
                onLoadMore();
            }
        };

        window.addEventListener("scroll", handleWindowScroll, { passive: true });
        handleWindowScroll();

        return () => {
            window.removeEventListener("scroll", handleWindowScroll);
        };
    }, [onLoadMore, items.length, loading]);

    return (
        <div className="frozen-sales-list-mobile">
            {empty && (
                <div className="frozen-sales-list-mobile-empty">{t("NoRows")}</div>
            )}

            {items.map((item, index) => (
                <div
                    key={`${item.noteNumber || "nonum"}-${item.lineNumber}-${index}`}
                    className="frozen-sales-list-card"
                >
                    <div className="frozen-sales-list-card-header">
                        <div className="frozen-sales-list-card-group">
                            {item.vesselName || "-"}
                        </div>
                        <div className="frozen-sales-list-card-date">
                            {item.landingTime ? (
                                <GrideDateFormatter
                                    date={item.landingTime as any}
                                    formatType="dd.MM.yyyy"
                                    noTd={true}
                                />
                            ) : (
                                "-"
                            )}
                        </div>
                    </div>

                    <div className="frozen-sales-list-card-body">
                        <FieldSplit
                            leftLabel={t("Note")}
                            leftValue={item.noteNumber || "-"}
                            rightLabel={t("Line")}
                            rightValue={item.lineNumber || "-"}
                        />
                        <FieldFull label={t("Product")} value={item.product || "-"} />
                        <FieldSplit
                            leftLabel={t("FishingGear")}
                            leftValue={item.fishingGear || "-"}
                            rightLabel={t("CatchField")}
                            rightValue={item.catchField || "-"}
                        />
                        <FieldFull label={t("Packaging")} value={item.packaging || "-"} />
                        <FieldSplit
                            leftLabel={t("AverageWeight")}
                            leftValue={formatWeight(item.averageLiveWeightKg)}
                            rightLabel={t("NumberOfPieces")}
                            rightValue={String(item.numberOfPieces ?? "-")}
                        />
                        <FieldSplit
                            leftLabel={t("Price")}
                            leftValue={formatCurrency(item.price)}
                            rightLabel={t("Amount")}
                            rightValue={formatCurrency(item.amount)}
                        />
                    </div>
                </div>
            ))}

            {loading && (
                <div className="frozen-sales-list-mobile-loading">{t("Loading")}</div>
            )}
        </div>
    );
};

export default SalesNoteJournalMobileList;